import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import PageRecord from "models/view-models/page-record";
import { ModalCloseButtonStyle } from "molecules/constants/modal-close-button-style";
import { ModalTypes } from "molecules/constants/modal-types";
import MenuButton from "molecules/menu-button/menu-button";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import { CopyPageModal } from "organisms/modals/copy-page-modal/copy-page-modal";
import EditPageModal from "organisms/modals/edit-page-modal/edit-page-modal";
import ManageCustomLogoModal from "organisms/modals/manage-custom-logo-modal/manage-custom-logo-modal";
import { GlobalPageShareLinkModal } from "organisms/modals/page-access-modal/global-page-share-link-modal";
import { PageAccessModal } from "organisms/modals/page-access-modal/page-access-modal";
import { RelocatePageModal } from "organisms/modals/relocate-page-modal/relocate-page-modal";
import CopyPageMenuItem from "pages/notebooks/pages/context-menu/copy-page-menu-item";
import DeletePageMenuItem from "pages/notebooks/pages/context-menu/delete-page-menu-item";
import EditPageMenuItem from "pages/notebooks/pages/context-menu/edit-page-menu-item";
import LogoMenuItem from "pages/notebooks/pages/context-menu/logo-menu-item";
import OpenPageMenuItem from "pages/notebooks/pages/context-menu/open-page-menu-item";
import RelocatePageMenuItem from "pages/notebooks/pages/context-menu/relocate-page-menu-item";
import SharePageMenuItem from "pages/notebooks/pages/context-menu/share-page-menu-item";
import usePageActions from "pages/notebooks/use-page-actions";
import React from "react";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { t } from "utilities/localization-utils";
import usePageActionPermission, {
    PageAction,
} from "../use-page-action-permission";
import { DataTestAttributes } from "interfaces/data-test-attributes";

interface PageContextMenuProps extends DataTestAttributes {
    page: PageRecord;
    isViewOnly: boolean;
    showShareAction?: boolean;
    showOpenAction?: boolean;
    showLogoAction?: boolean;
    menuButtonCssClassName?: string;
}

const CSS_CLASS_NAME = "c-page-context-menu";

const PageContextMenu = ({
    page,
    isViewOnly,
    showShareAction = false,
    showOpenAction = false,
    showLogoAction = false,
    menuButtonCssClassName = "",
    dataTestId,
}: PageContextMenuProps) => {
    const {
        deletePage,
        handleCopyPageModalClose,
        handleCopyPageModalOpen,
        handleDeleteConfirmModalClose,
        handleDeleteConfirmModalOpen,
        handlePageAccessModalClose,
        handlePageAccessModalOpen,
        handleRelocateModalClose,
        handleRelocateModalOpen,
        isRelocateModalOpen,
        isCopyPageModalOpen,
        isDeleteConfirmModalOpen,
        isPageAccessModalOpen,
        handleEditPageModalClose,
        handleEditPageModalOpen,
        isEditPageModalOpen,
        isNewlyPublicSharedPage,
        handlePageAccessModalSave,
        handleGlobalPageShareLinkModalClose,
        handleManageCustomLogoModalOpen,
        handleManageCustomLogoModalClose,
        isManageCustomLogoModalOpen,
    } = usePageActions(page);
    const { hasOnlyBasePermissions, can } = usePageActionPermission(page);

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );

    const modalType = isMobile ? ModalTypes.Bottom : ModalTypes.Base;

    const handleDeleteRequest = () => {
        handleDeleteConfirmModalOpen();
    };
    const handleCopyPageRequest = () => {
        handleCopyPageModalOpen();
    };
    const handleSharePageRequest = () => {
        handlePageAccessModalOpen();
    };

    const handleConfirmDelete = () => {
        deletePage();
    };

    //If a user only has base perms, we don't show the page context menu
    if (hasOnlyBasePermissions()) {
        return;
    }

    return (
        <div className={CSS_CLASS_NAME}>
            <MenuButton
                cssClassName={menuButtonCssClassName}
                buttonAccessibleText={t("actionsForItem", {
                    item: page.name,
                })}
                icon={Icons.MoreHorizontal}
                iconSize={IconSizes.Medium}
                dataTestId={dataTestId}>
                {showLogoAction && (
                    <LogoMenuItem
                        onClick={handleManageCustomLogoModalOpen}
                        page={page}
                    />
                )}
                <EditPageMenuItem
                    onClick={handleEditPageModalOpen}
                    page={page}
                />
                {showOpenAction && <OpenPageMenuItem page={page} />}
                {showShareAction && (
                    <SharePageMenuItem
                        onClick={handleSharePageRequest}
                        page={page}
                    />
                )}
                <CopyPageMenuItem onClick={handleCopyPageRequest} page={page} />
                <RelocatePageMenuItem
                    onClick={handleRelocateModalOpen}
                    page={page}
                />
                <DeletePageMenuItem onClick={handleDeleteRequest} page={page} />
            </MenuButton>
            <CopyPageModal
                page={page}
                isVisible={isCopyPageModalOpen}
                onClose={handleCopyPageModalClose}
            />

            {!isViewOnly && (
                <>
                    <ConfirmationModal
                        closeButtonStyle={ModalCloseButtonStyle.Hidden}
                        type={modalType}
                        confirmButtonText={t("yesAction", {
                            action: t("delete"),
                        })}
                        isVisible={isDeleteConfirmModalOpen}
                        label={t("page-deleteLabel")}
                        message={t("page-deleteMessage")}
                        onCancel={handleDeleteConfirmModalClose}
                        onConfirm={handleConfirmDelete}
                    />

                    {can(PageAction.ManageRecipientAccess) && (
                        <>
                            <PageAccessModal
                                isOpen={
                                    isPageAccessModalOpen &&
                                    !isNewlyPublicSharedPage
                                }
                                onClose={handlePageAccessModalClose}
                                onSaveSuccess={handlePageAccessModalSave}
                                pageId={page.id!}
                            />
                            <GlobalPageShareLinkModal
                                isOpen={isNewlyPublicSharedPage}
                                onClose={handleGlobalPageShareLinkModalClose}
                                pageId={page.id!}
                            />
                        </>
                    )}

                    <RelocatePageModal
                        page={page}
                        isVisible={isRelocateModalOpen}
                        onClose={handleRelocateModalClose}
                    />
                    <EditPageModal
                        page={page}
                        isVisible={isEditPageModalOpen}
                        closeDialog={handleEditPageModalClose}
                    />

                    <ManageCustomLogoModal
                        page={page}
                        isVisible={isManageCustomLogoModalOpen}
                        closeDialog={handleManageCustomLogoModalClose}
                    />
                </>
            )}
        </div>
    );
};

export default PageContextMenu;
